// tabs/LeadersTab.tsx
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActionIcon, Avatar, Badge, Button, Card, Center, Divider, Drawer,
  Group, Loader, Modal, ScrollArea, SimpleGrid, Stack, Text, ThemeIcon, Tooltip,
} from '@mantine/core';
import {
  IconMail, IconPhone, IconRefresh, IconSearch, 
  IconTrash, IconUserPlus, IconUsers, IconStarFilled
} from '@tabler/icons-react';
import { TextInput } from '@/components';
import { useCareCaseService, useLeaderService, CareCaseLeader, LeaderModelView } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';

interface LeadersTabProps {
  caseID: number;
  active: boolean;
  onCaseChange?: () => void;
}

const fullName = (l: LeaderModelView) =>
  [l.firstName, l.surname].filter(Boolean).join(' ').trim() || l.displayName?.trim() || l.email || l.userId;

const getInitials = (l: LeaderModelView) =>
  [l.firstName?.[0], l.surname?.[0]].filter(Boolean).join('').toUpperCase() || 'L';

// ── Assigned leader card ─────────────────────────────────────────────────────
function LeaderCard({
  leader,
  info,
  onRemove,
  removing,
}: {
  leader: CareCaseLeader;
  info?: LeaderModelView;
  onRemove: () => void;
  removing: boolean;
}) {
  const name = info ? fullName(info) : leader.userId;

  return (
    <Card withBorder radius="md" padding="lg" h="100%">
      <Group justify="center" mb="sm">
        <Avatar src={info?.avatar ?? undefined} alt={name} size="xl" radius="md">
          {info ? getInitials(info) : 'L'}
        </Avatar>
      </Group>

      <Group justify="center" gap={6} mb={4}>
        <Text fw={700} ta="center" size="md" lineClamp={1}>
          {name}
        </Text>
      </Group>

      <Group justify="center" mb={4}>
        {info?.typeName && (
          <Badge size="sm" color={info.typeColor ?? 'gray'} variant="light">
            {info.typeName}
          </Badge>
        )}
      </Group>
       <Group justify="center" mb="xs">
              {leader.isLead && (
                <Badge size="sm" variant="light" color="yellow" leftSection={<IconStarFilled size={12} />}>
                  Main Leader
                </Badge>
              )}
            </Group>

      <Group justify="flex-end" mt="md" gap={4}>
        <Tooltip label="Remove from case" withArrow>
          <ActionIcon
            variant="light"
            color="red"
            loading={removing}
            onClick={onRemove}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Card>
  );
}

// ── Search result card (inside drawer) ──────────────────────────────────────
function SearchResultCard({
  leader,
  addingId,
  onAdd,
}: {
  leader: LeaderModelView;
  addingId: string | null;
  onAdd: (isLead: boolean) => void;
}) {
  return (
    <Card withBorder radius="md" padding="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Group gap="md" wrap="nowrap" style={{ overflow: 'hidden', flex: 1 }}>
          <Avatar src={leader.avatar ?? undefined} radius="xl" size={44} color="teal">
            {getInitials(leader)}
          </Avatar>
          <Stack gap={2} style={{ overflow: 'hidden', flex: 1 }}>
            <Text fw={600} size="sm" truncate>{fullName(leader)}</Text>
            {leader.email && (
              <Group gap={6} wrap="nowrap">
                <IconMail size={13} style={{ flexShrink: 0, color: 'var(--mantine-color-dimmed)' }} />
                <Text size="xs" c="dimmed" truncate>{leader.email}</Text>
              </Group>
            )}
            {leader.phone && (
              <Group gap={6} wrap="nowrap">
                <IconPhone size={13} style={{ flexShrink: 0, color: 'var(--mantine-color-dimmed)' }} />
                <Text size="xs" c="dimmed">{leader.phone}</Text>
              </Group>
            )}
          </Stack>
        </Group>

        <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
          <Button
            size="xs"
            variant="light"
            loading={addingId === leader.userId}
            leftSection={<IconUserPlus size={14} />}
            onClick={() => onAdd(false)}
          >
            Add
          </Button>
          <Button
            size="xs"
            loading={addingId === leader.userId}
            onClick={() => onAdd(true)}
          >
            Add as Lead
          </Button>
        </Group>
      </Group>
    </Card>
  );
}

function LeaderCardSkeleton() {
  return (
    <Card withBorder radius="md" padding="md" h="100%">
      <Stack gap="sm">
        <Avatar radius="xl" size={52} color="gray">L</Avatar>
        <Stack gap={4}>
          <Text size="sm" c="dimmed">Loading…</Text>
          <Text size="xs" c="dimmed">—</Text>
          <Text size="xs" c="dimmed">—</Text>
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Add Leader Drawer ────────────────────────────────────────────────────────
function AddLeaderDrawer({
  opened,
  onClose,
  caseID,
  availableLeaders,
  onAdded,
}: {
  opened: boolean;
  onClose: () => void;
  caseID: number;
  availableLeaders: LeaderModelView[];
  onAdded: () => Promise<void> | void;
}) {
  const careCaseService = useCareCaseService();

  const [search, setSearch]     = useState('');
  const [addingId, setAddingId] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [filtered, setFiltered] = useState<LeaderModelView[]>([]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = search.trim().toLowerCase();
    if (!q) { setFiltered([]); return; }
    debounceRef.current = setTimeout(() => {
      setFiltered(
        availableLeaders.filter((l) =>
          fullName(l).toLowerCase().includes(q) || (l.email ?? '').toLowerCase().includes(q)
        )
      );
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search, availableLeaders]);

  const addToCase = async (leader: LeaderModelView, isLead: boolean) => {
    setAddingId(leader.userId);
    try {
      const ok = await careCaseService.assignLeader(caseID, leader.userId, isLead);
      if (ok) {
        await onAdded();
        toast.success('Leader Added', `${fullName(leader)} has been assigned to the case.`);
        setSearch('');
      } else {
        toast.error('Error', 'Could not add leader. Please try again.');
      }
    } catch (e) {
      console.error('Error adding leader:', e);
      toast.error('Error', 'Could not add leader. Please try again.');
    } finally {
      setAddingId(null);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={() => { onClose(); setSearch(''); }}
      title={
        <Group gap="xs">
          <IconUserPlus size={18} />
          <Text fw={600}>Add Leader</Text>
        </Group>
      }
      position="right"
      size={620}
      padding="md"
    >
      <Stack gap="md">
        <TextInput
          placeholder="Search leaders by name or email…"
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />

        {search.trim() && filtered.length > 0 && (
          <ScrollArea.Autosize mah={430}>
            <Stack gap="xs">
              {filtered.map((l) => (
                <SearchResultCard
                  key={l.userId}
                  leader={l}
                  addingId={addingId}
                  onAdd={(isLead) => addToCase(l, isLead)}
                />
              ))}
            </Stack>
          </ScrollArea.Autosize>
        )}

        {search.trim() && filtered.length === 0 && (
          <Card withBorder radius="md" padding="sm" >
            <Text size="sm" c="dimmed" ta="center">
              No leaders found for "{search}".
            </Text>
          </Card>
        )}

        {!search.trim() && (
          <>
            <Divider />
            <Text size="sm" c="dimmed" ta="center">
              Search above, then add a leader as a regular leader or as the case lead.
            </Text>
          </>
        )}
      </Stack>
    </Drawer>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function LeadersTab({ caseID, active, onCaseChange }: LeadersTabProps) {
  const careCaseService = useCareCaseService();
  const leaderService   = useLeaderService();

  const [caseLeaders, setCaseLeaders] = useState<CareCaseLeader[]>([]);
  const [allLeaders, setAllLeaders]   = useState<LeaderModelView[]>([]);
  const [loading, setLoading]         = useState(false);
  const [removingId, setRemovingId]   = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<CareCaseLeader | null>(null);

  const leaderMap = new Map(allLeaders.map((l) => [l.userId, l]));

  const fetchLeaders = useCallback(async () => {
    if (!caseID) return;
    setLoading(true);
    try {
      const [assigned, list] = await Promise.all([
        careCaseService.leaders(caseID),
        leaderService.list(),
      ]);
      setCaseLeaders(assigned ?? []);
      setAllLeaders(list ?? []);
    } catch (e) {
      console.error('Error fetching leaders:', e);
      toast.error('Error', 'Could not load leaders.');
    } finally {
      setLoading(false);
    }
  }, [caseID, careCaseService, leaderService]);

  useEffect(() => { if (active) fetchLeaders(); }, [active, fetchLeaders]);

  const handleAdded = async () => {
    await fetchLeaders();
    onCaseChange?.();
  };


  const handleConfirmRemove = async () => {
    if (!confirmTarget) return;
    const leader = confirmTarget;
    setRemovingId(leader.userId);
    try {
      const ok = await careCaseService.removeLeader(caseID, leader.userId);
      if (ok) {
        await fetchLeaders();
        onCaseChange?.();
        const info = leaderMap.get(leader.userId);
        toast.success('Removed', `${info ? fullName(info) : leader.userId} removed from the case.`);
      } else {
        toast.error('Error', 'Could not remove leader. Please try again.');
      }
    } catch (e) {
      console.error('Error removing leader:', e);
      toast.error('Error', 'Could not remove leader. Please try again.');
    } finally {
      setRemovingId(null);
      setConfirmTarget(null);
    }
  };

  const availableLeaders = allLeaders.filter(
    (l) => !caseLeaders.some((cl) => cl.userId === l.userId)
  );

  return (
    <Stack gap="lg">
      {/* ── Header: title + refresh + add button ── */}
      <Group justify="space-between" align="center">
        <Text fw={600} size="sm" c="dimmed">
          Leaders on this case ({caseLeaders.length})
        </Text>
        <Group gap="xs">
          <Tooltip label="Refresh leaders" withArrow>
            <ActionIcon
              variant="subtle"
              onClick={fetchLeaders}
              loading={loading}
              size="lg"
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
          <Button
            leftSection={<IconUserPlus size={16} />}
            onClick={() => setDrawerOpen(true)}
          >
            Add Leader
          </Button>
        </Group>
      </Group>

      {/* ── Leaders grid ── */}
      <Stack gap="xs">
        {loading ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            <LeaderCardSkeleton />
            <LeaderCardSkeleton />
            <LeaderCardSkeleton />
          </SimpleGrid>
        ) : caseLeaders.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <ThemeIcon size={56} radius="xl" variant="light" color="teal">
                <IconUsers size={30} />
              </ThemeIcon>
              <Text fw={600}>No leaders yet</Text>
              <Text size="sm" c="dimmed" ta="center" maw={360}>
                Click "Add Leader" above to assign a leader to this case.
              </Text>
            </Stack>
          </Center>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {caseLeaders.map((cl) => (
              <LeaderCard
                key={cl.caseLeaderID}
                leader={cl}
                info={leaderMap.get(cl.userId)}
                removing={removingId === cl.userId}
                onRemove={() => setConfirmTarget(cl)}
              />
            ))}
          </SimpleGrid>
        )}
      </Stack>

      <AddLeaderDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        caseID={caseID}
        availableLeaders={availableLeaders}
        onAdded={handleAdded}
      />

      {/* ── Remove confirmation modal ── */}
      <Modal
        opened={!!confirmTarget}
        onClose={() => setConfirmTarget(null)}
        title="Remove leader?"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to remove{' '}
            <Text span fw={600}>
              {confirmTarget ? (leaderMap.get(confirmTarget.userId) ? fullName(leaderMap.get(confirmTarget.userId)!) : confirmTarget.userId) : ''}
            </Text>{' '}
            from this case? This action cannot be undone.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setConfirmTarget(null)}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={!!confirmTarget && removingId === confirmTarget.userId}
              onClick={handleConfirmRemove}
            >
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}